import { findOrg } from '@/api/org-api'
import { ensureCanRunOnboarding } from '@/hooks/onboarding/use-onboarding'
import { organizationKeys } from '@/hooks/organization/query-keys'
import { ensureOrgAccess } from '@/shared/utils/route-utils/org-route-config'
import { useAuthStore } from '@/store/auth'
import { useOrgStore } from '@/store/org'
import { UserRole } from '@depanneo/types'
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { useEffect } from 'react'

export const Route = createFileRoute('/_authenticated/org/$orgId')({
  beforeLoad: async ({
    context: { queryClient },
    params: { orgId },
    abortController,
  }) => {
    // Member -> own org only; SUPER_ADMIN -> the URL's org becomes the active
    // one (deep links from Slack / email land on the right org). The signal
    // drops the switch if a newer navigation supersedes this one.
    await ensureOrgAccess(orgId, abortController.signal)

    const { role } = useAuthStore.getState()

    if (role === UserRole.ADMIN) {
      const org = await queryClient.ensureQueryData({
        queryKey: organizationKeys.detail(orgId),
        queryFn: () => findOrg(orgId),
      })

      // Only the onboarding owner is sent back (#2820): an admin invited while
      // the org is still being set up uses the app and never sees the wizard.
      if (
        org.onboardingCompletedAt === null &&
        (await ensureCanRunOnboarding(queryClient, orgId))
      ) {
        throw redirect({
          to: '/org/$orgId/onboarding',
          params: { orgId },
        })
      }
    }
  },
  loader: async ({ context: { queryClient }, params: { orgId } }) => {
    return await queryClient.ensureQueryData({
      queryKey: organizationKeys.detail(orgId),
      queryFn: () => findOrg(orgId),
    })
  },
  component: Component,
})

function Component() {
  const { setOrg } = useOrgStore()
  const org = Route.useLoaderData()
  useEffect(() => {
    if (org) {
      setOrg(org)
    }
  }, [org, setOrg])

  return <Outlet />
}

export default Component
